/**
 * External dependencies
 */
import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import {
	uapRecipeTemplateLibraryConfig,
	uapRtlCreateFilterEvent,
	uapRtlFilterTemplatesByCategory,
	uapRtlFindCategoryByProp,
} from '../../utilities';

/**
 * Styles
 */
import styles from './web-component-style.scss?inline';

/**
 * Defines <uap-rtl-category-menu> custom element
 */
@customElement( 'uap-rtl-category-menu' )
class UAPRTL_CategoryMenu extends LitElement {

	// Define style
	static styles = css`${unsafeCSS( styles )}`;

	// Properties
	@property( { type: Object, attribute: false } ) accessor baseResults = null;

	@property( { type: Object, attribute: false } ) accessor filter = {};

	@property( { type: Array, attribute: false } ) accessor menuItems = null;

	@property( { type: Object, attribute: false } ) accessor expandedItems = {};

	@property( { type: String, attribute: false } ) accessor activeSlug = '';

	@property( { type: Object, attribute: false } ) accessor isMobile = false;

	@property( { type: Object, attribute: false } ) accessor selectEl = null;

	// Library config.
	library = uapRecipeTemplateLibraryConfig;

	/**
	 * Trigger a category click
	 *
	 * @param {String} slug - The category slug
	 *
	 * @return {void}
	 */
	triggerCategoryClickBySlug( slug ) {
		const item = uapRtlFindCategoryByProp( 'slug', slug, this.library.categories );
		if ( item ) {
			this._handleCategoryClick( item );
		}
	}

	/**
	 * Set the menu based on the template IDs in baseResults
	 *
	 * @return {void}
	 */
	_setMenu() {
		this.menuItems = this.library.categories
			// Filter out categories that don't have any templates in baseResults
			.filter( category => {
				if ( category.slug === 'all' ) {
					return true;
				}
				// Check if any of the template IDs in the category exist in baseResults
				return category.templates.some( ID => this.baseResults.includes( ID ) );
			} )
			// Update the count property for each category
			.map( category => {
				// If the category is 'All', update the count to the baseResults length
				if ( category.slug === 'all' ) {
					category.count = this.baseResults.length;
					return category;
				}
				// Otherwise count the matches in the baseResults
				category.count = category.templates
					.filter( ID => this.baseResults.includes( ID ) ).length;
				return category;
			} );
	}

	/**
	 * Get the menu data
	 *
	 * @return {Array} The menu data
	 */
	_getMenuData() {
		// Check library readiness
		const isReady = this.library.isReady || false;
		if ( ! isReady ) {
			return [];
		}

		// If the menu is not set, set it to all categories
		if ( this.menuItems === null ) {
			this.menuItems = this.library.categories;
		}

		return this.menuItems;
	}

	/**
	 * Called when the component is updated
	 *
	 * @param {Map} changedProperties - The properties that changed
	 */
	updated( changedProperties ) {
		super.updated( changedProperties );

		// Check if baseResults property has changed
		if ( changedProperties.has( 'baseResults' ) ) {
			// If null set the baseResults to all template IDs.
			if ( this.baseResults === null ) {
				this.baseResults = this.library.templates.map( template => template.id );
			}
			// Set the menu based on the template IDs in baseResults
			this._setMenu();
		}

		// Check if the filter property has changed
		if ( changedProperties.has( 'filter' ) ) {
			let updatedSlug = '';
			switch ( this.filter.type ) {
				case 'category':
					updatedSlug = this.filter.filter.slug;
					break;
				case 'integration':
					updatedSlug = '';
					break;
				case 'search':
				case 'all':
					updatedSlug = 'all';
					break;
			}
			// Update the active states if the slug has changed.
			if ( updatedSlug !== this.activeSlug ) {
				this.activeSlug = updatedSlug;
				this.expandedItems = {};
			}
		}

		// Detect when isMobile property changes.
		if ( changedProperties.has( 'isMobile' ) ) {
			if ( ! this.mobile ) {
				// Close the Select2 dropdown if it is open
				if ( this.selectEl && jQuery( this.selectEl ).data( 'select2' ).isOpen() ) {
					jQuery( this.selectEl ).select2( 'close' );
				}
			}
			this.updateComplete.then( () => {
				this._handleSelect2();
			} );
		}

		// Reset select2 value when activeSlug is empty
		if ( changedProperties.has( 'activeSlug' ) && this.isMobile && this.selectEl ) {
			if ( this.filter.type !== 'category' ) {
				jQuery( this.selectEl ).val( '-1' ).trigger( 'change.select2' );
			}
		}
	}

	/**
	 * Handle select2
	 */
	_handleSelect2() {

		this.selectEl = this.shadowRoot.querySelector( 'select' );

		// Destroy select2 if it's not mobile
		if ( ! this.isMobile ) {
			if ( this.selectEl && ! jQuery( this.selectEl ).data( 'select2' ) ) {
				jQuery( this.selectEl ).select2( 'destroy' );
			}
			return;
		}

		// Initialize select2 if it's mobile
		if ( this.selectEl && ! jQuery( this.selectEl ).data( 'select2' ) ) {

			jQuery( this.selectEl ).select2( {
				dropdownParent: jQuery( '#uap-template-library__menu-wrapper' )
			} ).on( 'select2:select', e => {
				this._handleSelectChange( parseInt( e.params.data.id ) );
			} );

			// Add event listener to close the dropdown when clicking outside
			// Overcomming issues with select2 dropdown not closing due to Lit
			document.addEventListener( 'click', this._handleDocumentClick.bind( this ) );
		}

	}

	/**
	 * Handle document click - Close select on click outside menu container
	 *
	 * @param {Event} event - The event
	 */
	_handleDocumentClick( event ) {
		const menuContainer = document.getElementById( 'uap-template-library__menu-wrapper' );
		if ( ! menuContainer.contains( event.target ) ) {
			jQuery( this.selectEl ).select2( 'close' );
		}
	}

	/**
	 * Handle select onChange event
	 *
	 * @param {Number} id - The selected category id
	 *
	 * @return {void}
	 */
	_handleSelectChange( id ) {
		const item = uapRtlFindCategoryByProp( 'id', id, this.library.categories );
		this._handleCategoryClick( item );
	}

	/**
	 * Handle category click
	 *
	 * @param {Object} item - The clicked category object
	 *
	 * @return {void}
	 */
	_handleCategoryClick( item ) {
		this.activeSlug = item.slug;
		this._dispatchFilterChange( item );
	}

	/**
	 * Dispatch filter change event
	 *
	 * @param {Object} item - The category object
	 *
	 * @return {void}
	 */
	_dispatchFilterChange( item ) {
		const type = item.slug === 'all' ? 'all' : 'category';
		this.dispatchEvent(
			uapRtlCreateFilterEvent(
				type,
				item,
				uapRtlFilterTemplatesByCategory( item, this.library.templates )
			)
		);
	}

	/**
	 * Get category class
	 *
	 * @param {Object} item - The category object
	 *
	 * @return {String} The category class
	 */
	_getCategoryClass( item ) {
		let classes = [];
		if ( this._isActiveCategory( item ) ) {
			classes.push( 'uap-active' );
		}
		return classes.join( ' ' );
	}

	/**
	 * Check if the category is active
	 *
	 * @param {Object} item - The category object
	 *
	 * @return {Boolean} True if the category is active
	 */
	_isActiveCategory( item ) {
		return this.activeSlug === item.slug;
	}

	/**
	 * Render the template count
	 *
	 * @param {Object} item - The category object
	 * @param {Bool} showbrackets - Show brackets
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderTemplateCount( item, showbrackets = false ) {
		return showbrackets
			? html`<span class="count"> ( ${item.count} ) </span>`
			: html`<span class="count">${item.count}</span>`;
	}

	/**
	 * Render mobile menu - Select Dropdown
	 *
	 * @param {Array} items - The menu items
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderMobileMenu( items ) {

		// Extract the 'All' category from the list so it may be shown seperately.
		const allCategory = items.find( item => item.slug === 'all' );
		const categories = items.filter( item => item.slug !== 'all' );

		return html`
			<a 
				class="all-templates ${this._getCategoryClass( allCategory )}" 
				href="${allCategory.href}"
				@click=${e => {
					e.preventDefault();
					this._handleCategoryClick( allCategory );
				}}
			>
				${allCategory.name} ${this._renderTemplateCount( allCategory )}
			</a>
			
			<div class="uap-rtl-select2-wrapper">
				<select>
					<option value="${allCategory.id}">
						${ escapeHTML( __( 'Filter by category', 'uncanny-automator' ) ) }
					</option>
					${this._renderSelectOptions( categories )}
				</select>
			</div>
		`;
	}

	/**
	 * Render the select options
	 *
	 * @param {Array} items - The menu items
	 * @param {Number} level - The category level
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderSelectOptions( items, level = 0 ) {
		return items.map( item => html`
			<option value="${item.id}" ?selected=${this._isActiveCategory( item )}>
				${'-'.repeat( level )}${item.name} ${this._renderTemplateCount( item, true )}
			</option>
		` );
	}

	/**
	 * Render the menu - Default Desktop menu.
	 *
	 * @param {Array} items - The menu items
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderMenu( items ) {
		// Create a clone of the items
		items = structuredClone( items );

		// The first item is the "All categories" item. We'll add an exception for this one
		const allCategoriesItem = items.shift();

		/**
		 * The template of the menu item
		 *
		 * @param {Object} item - The category object
		 * @return {TemplateResult} The template of the menu item
		 */
		const filterMenuItem = item => {
			// Create item classes
			const itemClasses = [ 'filter-menu-item' ];

			// Add active class if the item is active
			if ( this._isActiveCategory( item ) ) {
				itemClasses.push( 'filter-menu-item--active' );
			}

			return html`
				<div 
					class="${ itemClasses.join( ' ' ) }"
					@click=${ event => {
						this._handleCategoryClick( item );
					} }
				>
					<div class="filter-menu-item-name">${ item.name }</div>
					<div class="filter-menu-item-count">
						${ this._renderTemplateCount( item ) }
					</div>
				</div>
			`;
		}

		return html`
			<div class="filter-group">
				<div class="filter-menu">
					<div class="filter-menu-items">
						${ filterMenuItem( allCategoriesItem ) }
					</div>
				</div>
			</div>

			<div class="filter-group">
				<div class="filter-menu">
					<div class="filter-menu-heading">
						${ escapeHTML( __( 'Categories', 'uncanny-automator' ) ) }
					</div>

					<div class="filter-menu-items">
						${
							repeat(
								items,
								( item, index ) => index,
								item => filterMenuItem( item )
							)
						}
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Render the component
	 *
	 * @return {TemplateResult} The template result
	 */
	render() {
		const menuData = this._getMenuData();
		if ( menuData.length === 0 ) {
			return html``;
		}
		return this.isMobile
			? html`${this._renderMobileMenu( menuData )}`
			: html`${this._renderMenu( menuData )}`;
	}
}